import React, { useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, UserPlus, X } from "lucide-react";

import { displayName as fullName, normalize } from "@/lib/names";

export const createProvisional = async (API, text, gender) => {
  const parts = text.trim().split(/\s+/);
  const last_name = parts.length > 1 ? parts.shift() : "";
  const { data } = await axios.post(`${API}/persons`, {
    first_name: parts.join(" "), last_name, gender: gender || "unknown", is_provisional: true,
  });
  return data;
};

export const PersonPicker = ({ value, onChange, options, gender, onCreated, testid, placeholder, allowCreate = true }) => {
  const { API } = useAuth();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const selected = options.find(p => p.id === value);

  const q = query.trim().toLowerCase();
  const matches = useMemo(() => options.filter(p => !q || `${fullName(p)} ${p.nickname || ""}`.toLowerCase().includes(q)).slice(0, 15), [options, q]);

  const create = async () => {
    setBusy(true);
    try {
      const p = await createProvisional(API, query, gender);
      onCreated?.(p);
      onChange(p.id);
      setQuery("");
      toast.success(`Fiche provisoire créée : ${fullName(p)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setBusy(false); setOpen(false); }
  };

  if (selected) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-[#E3D5CA] bg-[#FAF7F2]" data-testid={`${testid}-selected`}>
        <span className="flex-1 text-sm text-[#261911]">{fullName(selected)}</span>
        {selected.is_provisional && <ProvisionalTag />}
        <button type="button" onClick={() => onChange("")} className="text-[#993D22]" aria-label="Retirer" data-testid={`${testid}-clear`}>
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" data-testid={testid}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E5B4F]" />
      <Input value={query} placeholder={placeholder || "Rechercher ou saisir un nom"} className="pl-10 bg-[#FAF7F2]"
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={e => { setQuery(e.target.value); setOpen(true); }} data-testid={`${testid}-input`} />
      {open && (matches.length > 0 || q) && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto rounded-lg border border-[#E3D5CA] bg-[#FAF7F2] shadow-lg">
          {q && matches.length > 0 && <div className="px-3 pt-2 text-[11px] text-[#A96B18]">Profils existants — choisissez-en un pour éviter un doublon</div>}
          {matches.map(p => (
            <button key={p.id} type="button" onMouseDown={e => { e.preventDefault(); onChange(p.id); setQuery(""); }}
              className="w-full text-left px-3 py-2 hover:bg-[#F0E6D8] text-sm flex items-center justify-between" data-testid={`${testid}-option-${p.id}`}>
              <span>{fullName(p)} {p.is_provisional && <ProvisionalTag />}</span>
              <span className="text-xs text-[#6E5B4F]">{p.birth_year || ""}</span>
            </button>
          ))}
          {q && allowCreate && (
            <button type="button" disabled={busy} onMouseDown={e => { e.preventDefault(); create(); }}
              className="w-full text-left px-3 py-2 border-t border-[#E3D5CA] hover:bg-[#F0E6D8] text-sm text-[#993D22] flex items-center gap-2" data-testid={`${testid}-create`}>
              <UserPlus className="w-4 h-4" /> Créer « {query.trim()} » comme fiche provisoire
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const ProvisionalTag = () => (
  <span className="inline-block ml-1 px-1.5 py-0.5 rounded border border-dashed border-[#A96B18] text-[10px] uppercase tracking-wider text-[#A96B18]" data-testid="provisional-tag">
    Provisoire
  </span>
);

export default PersonPicker;
